import { PageLayout } from '@app/pages/layout/PageLayout'
import { useQuery } from '@based/sdk/react'
import { Blog } from '@schema/blog'
import { Title } from '@app/components/core/typography/Title'
import { Text } from '@app/components/core/typography/Text'
import { SafeHtml } from '@app/components/core/safeHtml/SafeHtml'
import Column from '@app/components/layout/Column'
import { styled } from 'inlines'
import { Author } from '@app/components/blocks/author/Author'
import useMediaQuery from '@app/util/useMediaQuery'
import { pages } from '@app/util/links'
import { useRoute } from 'wouter'
import { Stories } from '@schema/stories'
import { News } from '@schema/news'
import { Image } from '@app/components/core/image/Image'
import { expandImage } from '@app/types/StoredImage'
import { useTranslations } from '@i18n/useLanguage'
import useDateFormat from '@app/util/useDateFormat'

export type BlogItemPageProps = {
  blogId: string
}

const sharedParagraphStyles = {
  maxWidth: '50rem',
  marginLeft: 'auto',
  marginRight: 'auto',
  width: '100%',
}

const blogTypeToQueryMapping = {
  verhalen: 'view-stories',
  news: 'view-news',
  blog: 'view-blog',
}

export const BlogItemPage = ({ blogId }: BlogItemPageProps) => {
  const [, params] = useRoute<{ blogType: string }>(
    pages.blog(':blogType', ':blogId'),
  )
  const blogType = params?.blogType as 'verhalen' | 'news' | 'blog'
  const isMobile = useMediaQuery()
  const TitleComp = isMobile ? Title.H2 : Title.H1 //ENG-1026
  const t = useTranslations()
  const dateFormat = useDateFormat()
  const queryName = blogTypeToQueryMapping[blogType]
  const itemQuery = useQuery(queryName ?? null, { id: blogId })
  const item: Blog | Stories | News | undefined = itemQuery.data

  if (itemQuery.loading || !item) {
    return <PageLayout gap="2rem">{t.app.blogPage.loading}</PageLayout>
  }

  const { src, altText } = expandImage(item.images?.[0], `${item.title} image`)

  return (
    <>
      <PageLayout gap="0rem">
        <Column
          style={{
            ...sharedParagraphStyles,
            marginBottom: isMobile ? '2rem' : '4rem',
            marginTop: isMobile ? '2rem' : '2rem',
          }}
        >
          <div>
            <Text.Small style={{ fontWeight: 500, marginBottom: '0.25rem' }}>
              {dateFormat(new Date(item.publishedAt))}
            </Text.Small>
            <TitleComp style={{ margin: 0 }}>{item.title}</TitleComp>
          </div>
          <Text.Large>{item.intro}</Text.Large>
          <Author name={`${item.author.firstName} ${item.author.lastName}`} />
        </Column>

        {src && (
          <Image
            src={src}
            alt={altText}
            style={{
              borderRadius: '1.25rem',
              marginBottom: '2rem',
              maxHeight: isMobile ? '12rem' : '35rem',
              width: '100%',
              objectFit: 'cover',
            }}
          />
        )}

        <styled.div style={sharedParagraphStyles}>
          <SafeHtml html={item.content} />
        </styled.div>
      </PageLayout>
    </>
  )
}
